import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { analyzeText, createText, getText, updateText, listProfiles, exportPDF } from "../lib/api";
import TextRenderer from "../components/TextRenderer";
import { Save, Eye, Edit3, ArrowLeft, Check, ChevronDown, PlayCircle, Download } from "lucide-react";

export default function TextEditorPage() {
  const { textId } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [rawText, setRawText] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [docId, setDocId] = useState(textId || null);
  // Tab view for tablet/mobile: "edit" or "preview"
  const [activeTab, setActiveTab] = useState("edit");
  const [showProfilePicker, setShowProfilePicker] = useState(false);

  useEffect(() => {
    listProfiles()
      .then((res) => {
        setProfiles(res.data);
        if (!selectedProfile && res.data.length > 0) {
          setSelectedProfile(res.data[0].id);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (textId) {
      getText(textId)
        .then((res) => {
          setTitle(res.data.title);
          setRawText(res.data.raw_text);
          if (res.data.profile_id) setSelectedProfile(res.data.profile_id);
        })
        .catch(console.error);
    }
  }, [textId]);

  const doAnalyze = useCallback(async () => {
    if (!rawText.trim()) {
      setAnalysis(null);
      setCurrentProfile(null);
      return;
    }
    try {
      const res = await analyzeText(rawText, selectedProfile);
      setAnalysis(res.data.analysis);
      setCurrentProfile(res.data.profile);
    } catch (err) {
      console.error("Analysis failed:", err);
    }
  }, [rawText, selectedProfile]);

  useEffect(() => {
    const timer = setTimeout(doAnalyze, 400);
    return () => clearTimeout(timer);
  }, [doAnalyze]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (docId) {
        await updateText(docId, { title, raw_text: rawText, profile_id: selectedProfile });
      } else {
        const res = await createText(title, rawText, selectedProfile);
        setDocId(res.data.id);
        window.history.replaceState(null, "", `/editor/${res.data.id}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Save failed:", err);
    }
    setSaving(false);
  };

  const currentProfileName = profiles.find((p) => p.id === selectedProfile)?.name || "Choisir...";

  return (
    <div
      data-testid="editor-page"
      className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 page-enter has-bottom-nav lg:pb-6"
    >
      {/* Top bar: back + title + save */}
      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="editor-back-btn"
          onClick={() => navigate("/dashboard")}
          className="p-2.5 rounded-xl text-slate-400 active:text-slate-700 active:bg-slate-100 transition-all touch-target flex-shrink-0"
        >
          <ArrowLeft size={20} />
        </button>
        <input
          data-testid="editor-title-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titre du texte..."
          className="flex-1 font-fredoka text-xl md:text-2xl lg:text-3xl font-bold text-slate-800 bg-transparent border-none outline-none placeholder:text-slate-300 min-w-0"
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          {docId && (
            <>
              <button
                data-testid="editor-read-btn"
                onClick={() => navigate(`/reader/${docId}`)}
                className="flex items-center gap-1.5 font-fredoka font-bold py-2.5 px-4 md:py-3 md:px-5 rounded-full btn-bouncy transition-all bg-orange-500 active:bg-orange-600 text-white shadow-[0_4px_0_rgb(194,65,12)] touch-target text-sm md:text-base"
              >
                <PlayCircle size={16} />
                <span className="hidden sm:inline">Lire</span>
              </button>
              <button
                data-testid="editor-pdf-btn"
                onClick={async () => {
                  try {
                    const res = await exportPDF(rawText, title || "Texte", selectedProfile);
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${title || "texte"}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) { console.error("PDF export failed:", err); }
                }}
                className="flex items-center gap-1.5 font-fredoka font-bold py-2.5 px-4 md:py-3 md:px-5 rounded-full btn-bouncy transition-all bg-slate-600 active:bg-slate-700 text-white shadow-[0_4px_0_rgb(51,65,85)] touch-target text-sm md:text-base"
              >
                <Download size={16} />
                <span className="hidden sm:inline">PDF</span>
              </button>
            </>
          )}
          <button
            data-testid="editor-save-btn"
            onClick={handleSave}
            disabled={!title.trim() || saving}
          className={`flex items-center gap-1.5 font-fredoka font-bold py-2.5 px-4 md:py-3 md:px-6 rounded-full btn-bouncy transition-all flex-shrink-0 touch-target text-sm md:text-base ${
            saved
              ? "bg-green-500 text-white shadow-[0_4px_0_rgb(22,101,52)]"
              : title.trim()
                ? "bg-blue-500 active:bg-blue-600 text-white shadow-[0_4px_0_rgb(29,78,216)]"
                : "bg-slate-300 text-white cursor-not-allowed shadow-[0_4px_0_rgb(148,163,184)]"
          }`}
        >
          {saved ? <Check size={16} /> : <Save size={16} />}
          <span className="hidden sm:inline">{saving ? "Sauvegarde..." : saved ? "OK !" : "Sauvegarder"}</span>
          <span className="sm:hidden">{saved ? "OK" : <Save size={16} />}</span>
        </button>
        </div>
      </div>

      {/* Profile selector - compact for tablet */}
      <div className="mb-4 md:mb-6">
        {/* Mobile/Tablet: dropdown-style selector */}
        <div className="lg:hidden">
          <button
            data-testid="profile-picker-btn"
            onClick={() => setShowProfilePicker(!showProfilePicker)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl font-nunito font-bold text-sm text-slate-600 active:border-orange-300 transition-all w-full justify-between touch-target"
          >
            <span className="truncate">Profil : {currentProfileName}</span>
            <ChevronDown size={16} className={`transition-transform ${showProfilePicker ? "rotate-180" : ""}`} />
          </button>
          {showProfilePicker && (
            <div className="mt-2 bg-white border-2 border-slate-100 rounded-2xl shadow-lg p-2 space-y-1 page-enter">
              {profiles.map((p) => (
                <button
                  key={p.id}
                  data-testid={`profile-btn-${p.id}`}
                  onClick={() => { setSelectedProfile(p.id); setShowProfilePicker(false); }}
                  className={`w-full text-left px-4 py-3 rounded-xl font-nunito font-bold text-sm transition-all touch-target ${
                    selectedProfile === p.id
                      ? "bg-orange-100 text-orange-700"
                      : "text-slate-600 active:bg-slate-50"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop: horizontal pill selector */}
        <div className="hidden lg:block">
          <label className="font-nunito font-bold text-sm text-slate-500 uppercase tracking-wider mb-3 block">
            Profil de lecture
          </label>
          <div className="flex flex-wrap gap-3" data-testid="profile-selector">
            {profiles.map((p) => (
              <button
                key={p.id}
                data-testid={`profile-btn-desktop-${p.id}`}
                onClick={() => setSelectedProfile(p.id)}
                className={`px-4 py-2 rounded-xl font-nunito font-bold text-sm transition-all ${
                  selectedProfile === p.id
                    ? "bg-orange-500 text-white shadow-md"
                    : "bg-white border-2 border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tablet/Mobile: Tab toggle (edit / preview) */}
      <div className="lg:hidden flex mb-4 bg-slate-100 p-1 rounded-xl">
        <button
          data-testid="tab-edit"
          onClick={() => setActiveTab("edit")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-nunito font-bold text-sm transition-all touch-target ${
            activeTab === "edit"
              ? "bg-white text-blue-600 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <Edit3 size={16} />
          Ecrire
        </button>
        <button
          data-testid="tab-preview"
          onClick={() => setActiveTab("preview")}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-nunito font-bold text-sm transition-all touch-target ${
            activeTab === "preview"
              ? "bg-white text-orange-600 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <Eye size={16} />
          Apercu
          {rawText.trim() && (
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          )}
        </button>
      </div>

      {/* Editor content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Input - visible on mobile when "edit" tab, always on desktop */}
        <div className={`${activeTab === "edit" ? "block" : "hidden"} lg:block`}>
          <div className="hidden lg:flex items-center gap-2 mb-3">
            <Edit3 size={16} className="text-blue-500" />
            <span className="font-nunito font-bold text-sm text-slate-500 uppercase tracking-wider">
              Texte original
            </span>
          </div>
          <textarea
            data-testid="editor-textarea"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Saisissez ou collez votre texte ici..."
            className="w-full bg-white border-3 md:border-4 border-blue-100 rounded-2xl p-4 md:p-6 text-lg md:text-xl font-lexend leading-relaxed md:leading-loose focus:outline-none focus:border-blue-400 transition-colors placeholder:text-slate-300 shadow-inner resize-y custom-scroll"
            style={{ minHeight: "calc(100vh - 320px)", maxHeight: "calc(100vh - 200px)" }}
          />
        </div>

        {/* Preview - visible on mobile when "preview" tab, always on desktop */}
        <div className={`${activeTab === "preview" ? "block" : "hidden"} lg:block`}>
          <div className="hidden lg:flex items-center gap-2 mb-3">
            <Eye size={16} className="text-orange-500" />
            <span className="font-nunito font-bold text-sm text-slate-500 uppercase tracking-wider">
              Apercu adapte
            </span>
          </div>
          <div
            data-testid="editor-preview"
            className="w-full bg-white border-3 md:border-4 border-orange-100 rounded-2xl p-4 md:p-6 shadow-inner overflow-y-auto custom-scroll"
            style={{ minHeight: "calc(100vh - 320px)", maxHeight: "calc(100vh - 200px)" }}
          >
            <TextRenderer analysis={analysis} profile={currentProfile} />
          </div>
        </div>
      </div>
    </div>
  );
}
